/**
 * Face Recognition API Service
 * Handles all API calls to the face recognition backend
 */
import axios from 'axios';
import apiConfig from '../api/apiConfig';
import axiosInstance from '../api/axiosInstance';

const FACE_RECOGNITION_API = `${apiConfig.baseURL}/face-recognition`;

/**
 * Set a reference person for face recognition in a session
 * @param {string} sessionId - ID of the session
 * @param {string} imageId - ID of the reference image
 * @param {string} personName - Name of the reference person
 * @param {boolean} isPrimary - Whether this should be the primary photo
 * @returns {Promise<Object>} API response
 */
const setReferencePerson = async (sessionId, imageId, personName, isPrimary = false) => {
  try {
    const response = await axiosInstance.post(
      `${FACE_RECOGNITION_API}/reference`,
      {
        sessionId,
        imageId,
        personName,
        isPrimary
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error setting reference person:', error);
    throw error;
  }
};

/**
 * Find the reference person across all images in a session
 * @param {string} sessionId - ID of the session
 * @returns {Promise<Object>} API response with matching results
 */
const findPersonInSession = async (sessionId) => {
  try {
    const response = await axiosInstance.get(`${FACE_RECOGNITION_API}/find/${sessionId}`);
    return response.data;
  } catch (error) {
    console.error('Error finding person in session:', error);
    throw error;
  }
};

/**
 * Remove face recognition data for a session
 * @param {string} sessionId - ID of the session
 * @returns {Promise<Object>} API response
 */
const removeFaceRecognition = async (sessionId) => {
  try {
    const response = await axiosInstance.delete(`${FACE_RECOGNITION_API}/${sessionId}`);
    return response.data;
  } catch (error) {
    console.error('Error removing face recognition data:', error);
    throw error;
  }
};

/**
 * Get all reference persons for a session
 * @param {string} sessionId - ID of the session
 * @returns {Promise<Object>} API response with reference persons
 */
const getSessionReferencePersons = async (sessionId) => {
  try {
    console.log(`🔍 Fetching reference persons for session: ${sessionId}`);
    console.log(`🔗 API URL: ${FACE_RECOGNITION_API}/reference/${sessionId}`);
    const response = await axiosInstance.get(`${FACE_RECOGNITION_API}/reference/${sessionId}`);
    console.log('✅ Reference persons fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error getting reference persons:', error);
    console.error('Error response:', error.response?.data);
    console.error('Error status:', error.response?.status);
    console.error('Error headers:', error.response?.headers);
    throw error;
  }
};

/**
 * Find matches for a specific reference person
 * @param {string} sessionId - ID of the session
 * @param {string} referencePersonId - ID of the reference person
 * @returns {Promise<Object>} API response with matching results
 */
const findPersonMatches = async (sessionId, referencePersonId) => {
  try {
    const response = await axiosInstance.get(`${FACE_RECOGNITION_API}/matches/${sessionId}/${referencePersonId}`);
    return response.data;
  } catch (error) {
    console.error('Error finding person matches:', error);
    throw error;
  }
};

/**
 * Delete a reference person
 * @param {string} sessionId - ID of the session
 * @param {string} referencePersonId - ID of the reference person
 * @returns {Promise<Object>} API response
 */
const deleteReferencePerson = async (sessionId, referencePersonId) => {
  try {
    const response = await axiosInstance.delete(`${FACE_RECOGNITION_API}/reference/${sessionId}/${referencePersonId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting reference person:', error);
    throw error;
  }
};

/**
 * Test the AWS Rekognition connection
 * @returns {Promise<Object>} Connection test results
 */
const testAwsConnection = async () => {
  try {
    console.log('🧪 Testing AWS Rekognition connection');
    const response = await axiosInstance.get(`${FACE_RECOGNITION_API}/test-connection`);
    console.log('✅ AWS Rekognition connection test successful:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error testing AWS Rekognition connection:', error);
    console.error('Error response:', error.response?.data);
    console.error('Error status:', error.response?.status);
    throw error;
  }
};

/**
 * Find person matches in all session photos using a reference photo
 * @param {string} sessionId - ID of the session
 * @param {string} referenceImageBase64 - Base64 encoded reference image
 * @returns {Promise<Object>} API response with matching results
 */
const findPersonMatchesInPhotos = async (sessionId, referenceImageBase64) => {
  try {
    console.log(`🔍 Searching for person matches in session ${sessionId}`);
    const response = await axiosInstance.post(
      `${FACE_RECOGNITION_API}/find-in-photos/${sessionId}`,
      { referenceImageBase64 }
    );
    return response.data;
  } catch (error) {
    console.error('Error finding person matches in photos:', error);
    throw error;
  }
};

/**
 * Find matches for all reference persons in a session (Enhanced)
 * @param {string} sessionId - ID of the session
 * @returns {Promise<Object>} API response with matching results
 */
const findMatchesWithReferencePersons = async (sessionId) => {
  try {
    console.log(`🔍 Finding matches with reference persons for session ${sessionId}`);
    const response = await axiosInstance.post(
      `${FACE_RECOGNITION_API}/find-with-reference-persons/${sessionId}`,
      {
        mainFacesOnly: true,
        showAllFaces: true,  // Enable multi-person detection
        overrideProcessed: false
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error finding matches with reference persons:', error);
    throw error;
  }
};

/**
 * Remove a specific reference photo from a person
 * @param {string} sessionId - ID of the session
 * @param {string} imageId - ID of the image to remove as reference
 * @returns {Promise<Object>} API response
 */
const removeReferencePhoto = async (sessionId, imageId) => {
  try {
    console.log(`🗑️ Removing reference photo ${imageId} from session ${sessionId}`);
    const response = await axiosInstance.delete(
      `${FACE_RECOGNITION_API}/reference-photo/${sessionId}/${imageId}`
    );
    return response.data;
  } catch (error) {
    console.error('Error removing reference photo:', error);
    throw error;
  }
};

/**
 * Set a photo as the primary reference photo for a person
 * @param {string} sessionId - ID of the session
 * @param {string} imageId - ID of the image to set as primary
 * @returns {Promise<Object>} API response
 */
const setPrimaryReferencePhoto = async (sessionId, imageId) => {
  try {
    console.log(`⭐ Setting primary reference photo ${imageId} for session ${sessionId}`);
    const response = await axiosInstance.put(
      `${FACE_RECOGNITION_API}/reference-photo/primary/${sessionId}/${imageId}`
    );
    return response.data;
  } catch (error) {
    console.error('Error setting primary reference photo:', error);
    throw error;
  }
};

/**
 * Rename a reference person
 * AWS Rekognition does not support updating ExternalImageId — the backend deletes the
 * old face(s) from the collection and re-indexes them under the new name.
 * @param {string} sessionId - ID of the session
 * @param {string} referencePersonId - uniqueId of the reference person
 * @param {string} newName - New name for the person
 * @returns {Promise<Object>} API response with updated person data
 */
const renameReferencePerson = async (sessionId, referencePersonId, newName) => {
  try {
    console.log(`✏️ Renaming reference person ${referencePersonId} to "${newName}"`);
    const response = await axiosInstance.put(
      `${FACE_RECOGNITION_API}/reference/${sessionId}/${referencePersonId}/rename`,
      { newName }
    );
    return response.data;
  } catch (error) {
    console.error('Error renaming reference person:', error);
    throw error;
  }
};

/**
 * Find matches for all reference persons in a specific image
 * @param {string} sessionId - ID of the session
 * @param {string} imageId - ID of the specific image
 * @returns {Promise<Object>} API response with matching results for the specific image
 */
/**
 * Find matches for reference persons in a specific image with search options
 * @param {string} sessionId - ID of the session
 * @param {string} imageId - ID of the specific image
 * @param {Object} options - Search options
 * @param {string} options.searchMode - 'main', 'all', or 'both'
 * @param {boolean} options.searchMainFace - Whether to search main face
 * @param {boolean} options.searchAllFaces - Whether to search all faces
 * @returns {Promise<Object>} API response with matching results
 */
const findMatchesInImage = async (sessionId, imageId, options = {}) => {
  try {
    const requestBody = {
      searchMode: options.searchMode || 'both',
      searchMainFace: options.searchMainFace !== false,
      searchAllFaces: options.searchAllFaces !== false
    };
    
    const response = await axiosInstance.post(
      `${FACE_RECOGNITION_API}/find-in-image/${sessionId}/${imageId}`,
      requestBody
    );
    return response.data;
  } catch (error) {
    console.error('Error finding matches in specific image:', error);
    throw error;
  }
};

const faceRecognitionService = {
  setReferencePerson,
  findPersonInSession,
  removeFaceRecognition,
  getSessionReferencePersons,
  findPersonMatches,
  deleteReferencePerson,
  testAwsConnection,
  findPersonMatchesInPhotos,
  findMatchesWithReferencePersons,
  findMatchesInImage,
  removeReferencePhoto,
  setPrimaryReferencePhoto,
  renameReferencePerson
};

export default faceRecognitionService;

export {
  setReferencePerson,
  findPersonInSession,
  removeFaceRecognition,
  getSessionReferencePersons,
  findPersonMatches,
  deleteReferencePerson,
  testAwsConnection,
  findPersonMatchesInPhotos,
  findMatchesWithReferencePersons,
  findMatchesInImage,
  removeReferencePhoto,
  setPrimaryReferencePhoto,
  renameReferencePerson
};
